import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './components/ErrorFallback';
import AdminSkeleton from './components/skeletons/AdminSkeleton';
import DashboardSkeleton from './components/skeletons/DashboardSkeleton';
import { Listing, User, Notification, ChatMessage, Favorite, ListingStatus, NotificationType, NotificationCategory } from './types';
import NotificationCenter from './components/NotificationCenter';
// DemoBadge removed
import { PAYMENT_CONFIG } from './lib/payment/config';

// Eager load Home for performance (LCP)
import Marketplace from './components/Marketplace';
import SplashScreen from './components/SplashScreen';
import SupportWidget from './components/SupportWidget';
// LogoIcon import removed
import { notify } from './lib/toast';

// Lazy load heavy components
// 14. Lazy load heavy components
const ChatModal = lazy(() => import('./components/ChatModal'));



const InstantBuyWizard = lazy(() => import('./components/wizard/InstantBuyWizard'));
const SellChoice = lazy(() => import('./components/wizard/SellChoice'));

// Preloading Helpers
const preloadAdmin = () => import('./components/AdminPanel');
const preloadDashboard = () => import('./components/UserDashboard');
const preloadSell = () => import('./components/wizard/ListingFormWizard');
const preloadInstant = () => import('./components/wizard/InstantBuyWizard');
const preloadSellChoice = () => import('./components/wizard/SellChoice');

const ListingFormWizard = lazy(() => import('./components/wizard/ListingFormWizard'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

const WalletPage = lazy(() => import('./components/WalletPage'));
const DepositPage = lazy(() => import('./pages/DepositPage'));
const ManualEscrowReleaseList = lazy(() => import('./components/ManualEscrowReleaseList'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const Auth = lazy(() => import('./components/Auth'));
const ListingDetails = lazy(() => import('./components/ListingDetails'));
const Messages = lazy(() => import('./components/Messages'));
const WithdrawalPage = lazy(() => import('./components/WithdrawalPage'));
const EditListingPage = lazy(() => import('./pages/EditListingPage'));
const ToastTest = lazy(() => import('./components/ToastTest'));
const BuggyComponent = lazy(() => import('./components/BuggyComponent'));
const SEO = lazy(() => import('./components/SEO'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const HelpCenterPage = lazy(() => import('./components/HelpCenterPage'));
const AccountRestrictionsPage = lazy(() => import('./components/AccountRestrictionsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const NotificationPreferencesPage = lazy(() => import('./components/NotificationPreferencesPage'));
const SearchPage = lazy(() => import('./components/SearchPage'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const FavoritesPage = lazy(() => import('./components/favorites/FavoritesPage'));
const FlashSalePage = lazy(() => import('./pages/FlashSalePage'));
const AuthAction = lazy(() => import('./components/AuthAction'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

/* Mobile Phase 14 Routes */
const MobileDashboard = lazy(() => import('./components/mobile/MobileDashboard'));
const NotificationsPage = lazy(() => import('./components/mobile/NotificationsPage'));
const FollowingPage = lazy(() => import('./components/mobile/FollowingPage'));

const LoadingFallback = () => (
  <div className="min-h-screen bg-[#FBFBFD] flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-gray-100 border-t-brand-accent rounded-full animate-spin"></div>
  </div>
);
import { auth, db } from './lib/firebase';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, updateDoc, getDocs, limit, addDoc } from 'firebase/firestore';

const mapUser = (u: any): User => ({
  id: u.id,
  name: u.name || u.email?.split('@')[0],
  username: u.username,
  email: u.email,
  role: u.role || 'user',
  avatarUrl: u.avatar_url,
  balance: Number(u.balance) || 0,
  joinDate: Number(u.join_date) || Date.now(),
  bio: u.bio,
  gender: u.gender,
  birthDate: u.birth_date,
  totalSales: u.total_sales || 0,
  followersCount: u.followers_count || 0,
  lastIp: u.last_ip,
  riskScore: u.risk_score || 100,
  riskStatus: u.risk_status || 'SAFE',
  verifiedIbans: u.verified_ibans || [],
  userType: u.user_type || 'INDIVIDUAL',
  city: u.city,
  district: u.district,
  successfulTradesCount: u.successful_trades_count || 0,
  avgResponseTime: u.avg_response_time || 'Aynı gün',
  isIdentityVerified: u.is_identity_verified || false,
  lastSeen: u.last_seen || u.last_active,
  isVacationMode: u.is_vacation_mode || false,
  preferredCarrier: u.preferred_carrier,
  pinnedListingIds: u.pinned_listing_ids || [],
  preferredRegion: u.preferredRegion || 'TR',
  preferredLanguage: u.preferredLanguage || 'tr-TR',
  preferredCurrency: u.preferredCurrency || 'TRY'
});

const mapListing = (l: any): Listing => ({
  id: l.id,
  title: l.title,
  description: l.description,
  price: Number(l.price),
  category: l.category,
  categoryId: l.category_id || l.category,
  subCategoryId: l.sub_category || l.sub_category_id,
  brand: l.brand,
  condition: l.condition,
  type: l.type as any,
  status: l.status as any,
  imageUrls: l.image_urls || [],
  sellerId: l.seller_id,
  createdAt: Number(l.created_at),
  updatedAt: Number(l.updated_at),
  visualAnalysis: l.visual_analysis,
  favoritesCount: Number(l.favorites_count) || 0,
  boostedUntil: l.boosted_until ? Number(l.boosted_until) : undefined,
  offerAmount: l.offer_amount ? Number(l.offer_amount) : undefined
});

// ... imports
import CartPage from './pages/CartPage';
// Providers
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { useAuth } from './contexts/AuthContext';
import { useFavorites } from './hooks/useFavorites';
// ... other imports

// ... inside App component return

import { useHaptics } from './hooks/useHaptics';
import { useSwipeBack } from './hooks/useSwipeBack';
import { usePushNotifications } from './hooks/usePushNotifications';


// Mobile Components
import MobileHeader from './components/mobile/MobileHeader';
import BottomNav from './components/mobile/BottomNav';
import LegalFooter from './components/ui/LegalFooter';
import { useCart } from './contexts/CartContext';
import { ThemeProvider } from './contexts/ThemeContext';

// 🌍 i18n Infrastructure
import './src/i18n/config';  // Initialize i18next
import { RegionProvider, useRegion } from './src/contexts/RegionContext';
import RegionSelectorModal from './components/ui/RegionSelectorModal';
import { AdminProvider } from './contexts/AdminContext';

const CartBadge: React.FC = () => {
  const { items } = useCart();
  const count = Object.keys(items).length;
  if (count === 0) return null;
  return (
    <span className="absolute -top-0.5 -right-0.5 bg-brand-terracotta text-white text-[9px] font-bold flex items-center justify-center rounded-full w-3.5 h-3.5 shadow-sm">
      {count}
    </span>
  );
};

// ... other imports

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <RegionProvider>
          <CartProvider>
            <AuthProvider>
              <AdminProvider>
                <AppContent />
              </AdminProvider>
            </AuthProvider>
          </CartProvider>
        </RegionProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { region, needsSelection } = useRegion();
  // listings state removed
  const [users, setUsers] = useState<User[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Use Context instead of local state for auth
  const { currentUser, loading: authLoading, refreshUser } = useAuth();
  const { favorites: globalFavorites, toggleFavorite: globalToggleFavorite } = useFavorites();

  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const { trigger: haptic, selection } = useHaptics();

  // Phase 2: Global Push Notification Registration (Expo & FCM)
  usePushNotifications(currentUser?.id);

  // Phase 18.5: Native Gestures & Polish
  useSwipeBack();

  // Phase 19.5: Launch Experience
  const [showSplash, setShowSplash] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Splash Logic
    const timer = setTimeout(() => {
      setShowSplash(false);

      const hasSeen = localStorage.getItem('hasSeenOnboarding');
      if (!hasSeen) {
        setShowOnboarding(true);
      }
    }, 6500); // Enforced Premium Duration (6.5s for stability)

    // App Resume Logic (Optional - forcing splash only on cold start provided by default browser behavior)
    // If we wanted to hide splash on tab switch, we'd check visibilityState, but here we just want it on mount.

    return () => clearTimeout(timer);
  }, []);

  const handleOnboardingComplete = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };

  useEffect(() => {
    // Detect iOS for conditional overscroll
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    if (isIOS) {
      document.body.classList.add('ios-device');
    }

    return () => {
      document.body.classList.remove('ios-device');
    };
  }, []);

  // 🌍 Dynamic Regional Theme Injection
  useEffect(() => {
    // Remove previous region classes
    const classes = document.body.className.split(' ').filter(c => !c.startsWith('region-'));
    document.body.className = [...classes, `region-${region}`].join(' ');
  }, [region]);

  useEffect(() => {
    if (PAYMENT_CONFIG.IS_DEMO_MODE) {
      console.log(
        '%c🧪 FASTCASH TRADE - DEMO MODU AKTİF %cGerçek ödeme işlemleri yapılmaz, sadece simülasyon çalışır.',
        'background: #fbbf24; color: #000; font-weight: bold; padding: 4px 8px; border-radius: 4px;',
        'color: #d97706; font-style: italic;'
      );
    }
  }, []);

  // Phase 14: Global Navigation Listener for Deep Links
  useEffect(() => {
    const handleGlobalNav = (e: any) => {
      if (e.detail) {
        navigate(e.detail);
      }
    };
    window.addEventListener('app-navigate', handleGlobalNav);
    return () => window.removeEventListener('app-navigate', handleGlobalNav);
  }, [navigate]);

  const handleUpdateListingStatus = async (id: string, status: ListingStatus) => {
    await updateDoc(doc(db, 'listings', id), { status });
  };

  const handleUpdateOrderShipping = async (orderId: string, carrier: string, trackingCode: string) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'SHIPPED',
        shippingInfo: { carrier, trackingCode },
        shippedAt: Date.now()
      });

      const orderSnap = await getDoc(orderRef);
      if (orderSnap.exists()) {
        const data = orderSnap.data();
        await addDoc(collection(db, 'notifications'), {
          user_id: data.buyerId,
          type: 'SYSTEM',
          data: { message: `Siparişiniz kargolandı! ${carrier} - ${trackingCode}` },
          created_at: Date.now(),
          is_read: false
        });
      }
      notify.success("Kargo bilgileri güncellendi.");
    } catch (error) {
      console.error("Shipping update error:", error);
      notify.error("Kargo kadeylilemedi.");
    }
  };

  // Performance Mode
  const [isLowPerf, setIsLowPerf] = useState(() => localStorage.getItem('fastcashtrade_low_perf') === 'true');

  useEffect(() => {
    if (isLowPerf) {
      document.body.classList.add('low-perf');
    } else {
      document.body.classList.remove('low-perf');
    }
    localStorage.setItem('fastcashtrade_low_perf', String(isLowPerf));
  }, [isLowPerf]);

  const handleConfirmDelivery = async (orderId: string) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'DELIVERED',
        deliveredAt: Date.now()
      });
      // Notify Seller
      const orderSnap = await getDoc(orderRef);
      if (orderSnap.exists()) {
        const data = orderSnap.data();
        await addDoc(collection(db, 'notifications'), {
          user_id: data.sellerId,
          type: 'SYSTEM',
          data: { message: `Sipariş teslim alındı! Ödeme onayı bekleniyor.` },
          created_at: Date.now(),
          is_read: false
        });
      }
      notify.success("Teslimat onaylandı.");
    } catch (error) {
      console.error("Delivery confirm error:", error);
      notify.error("İşlem hatası.");
    }
  };

  const fetchData = async () => {
  };

  // Realtime Users (Still needed for profiles/avatars globally for now)
  useEffect(() => {
    if (!currentUser) {
      setUsers([]);
      return;
    }

    const unsubUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      setUsers(snapshot.docs.map(d => mapUser({ id: d.id, ...d.data() })));
    }, (error) => {
      console.log("Users listener silent error:", error.code);
    });

    return () => {
      unsubUsers();
    };
  }, [currentUser]);

  // Note: Listings fetch moved to individual components for pagination (Phase 5.3)

  // Notifications Realtime
  useEffect(() => {
    if (currentUser) {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', currentUser.id),
        where('isArchived', '==', false),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const loadedNotifications = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            userId: data.user_id || data.userId,
            category: data.category || 'SYSTEM',
            type: data.type || 'SYSTEM',
            priority: data.priority || 'MEDIUM',
            title: data.title || 'Bildirim',
            message: data.message || data.data?.message || '',
            isRead: data.is_read || data.isRead || false,
            isArchived: data.isArchived || false,
            isActionable: data.isActionable || false,
            channels: data.channels || { inApp: true, email: false, push: false, sms: false },
            deliveryStatus: data.deliveryStatus || { inApp: 'delivered' },
            createdAt: data.created_at || data.createdAt || Date.now(),
            actorId: data.actor_id || data.actorId,
            listingId: data.listing_id || data.listingId,
            data: data.data,
          } as Notification;
        });
        setNotifications(loadedNotifications);
      }, (error) => {
        console.error("Notification listener error:", error);
        setNotifications([]);
      });

      return () => unsubscribe();
    } else {
      setNotifications([]);
    }
  }, [currentUser?.id]);

  // Close notifications on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadMessageCount = notifications.filter(n => !n.isRead && (n.type as string === 'MESSAGE' || n.type === NotificationType.NEW_MESSAGE)).length;
  const unreadSystemCount = notifications.filter(n => !n.isRead && !(n.type as string === 'MESSAGE' || n.type === NotificationType.NEW_MESSAGE)).length;

  const handleMarkAllRead = async () => {
    if (currentUser) {
      // Only mark system notifications as read here
      const batchPromises = notifications
        .filter(n => !n.isRead && !(n.type as string === 'MESSAGE' || n.type === NotificationType.NEW_MESSAGE))
        .map(n => updateDoc(doc(db, 'notifications', n.id), { is_read: true }));

      await Promise.all(batchPromises);
    }
  };

  // const unreadCount = notifications.filter(n => !n.isRead).length; // Removed/Replaced

  // Render
  // removed: if (authLoading) return <LoadingFallback />;

  // ... (Header code skipped, assuming it relies on currentUser/users but NOT listings directly, 
  // except maybe notifications? Notification mapping uses listings.find currently!)

  // FIX: Notifications use listings.find. We need to fetch listing details for notifications or just use ID/Placeholder.
  // Notification rendering in App.tsx (lines 275-300) uses listings.find.
  // Since we removed listings, this will break.
  // We should probably fetch notification listings or just show "Item" if missing.
  // For Phase 5.3, let's keep it simple: Show "View Item" or placeholder title if listing not found.
  // Or fetch listings referenced in notifications?
  // Let's implement a quick map for notifications if we want to be fancy, but "Details" link is enough usually.
  // However, the notification text uses listing title.
  // map(n => ... listing.title)
  // I will update the notification rendering to handle missing listing gracefully.

  return (
    <div className="min-h-screen bg-brand-offWhite text-brand-clay font-sans pb-24 md:pb-0 transition-colors duration-500">
      <AnimatePresence mode="wait">
        {authLoading ? (
          <motion.div
            key="auth-loading"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <LoadingFallback />
          </motion.div>
        ) : (
          <motion.div
            key="main-app"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="contents"
          >
            <Suspense fallback={null}>
              <SEO
                title="Ana Sayfa"
                description="FastCashTrade ile ikinci el eşyalarını güvenle al ve sat. Hızlı, kolay ve güvenli ticaret pazaryeri."
              />
            </Suspense>


            {/* Launch Experience */}
            <AnimatePresence>
              {showSplash && (
                <SplashScreen key="splash" />
              )}
              {showOnboarding && !showSplash && (
                <motion.div
                  key="onboarding"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[8000]"
                >
                  <Suspense fallback={null}>
                    <Onboarding onComplete={handleOnboardingComplete} />
                  </Suspense>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ... Toaster ... */}
            <SupportWidget />

            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                className: '!p-0 !bg-transparent !shadow-none',
              }}
            />

            {/* Üst Header (Desktop Only) - on Mobile show only if NOT on Listing Details */}
            {!showSplash && (
              <>
                <div className="md:hidden">
                  {!location.pathname.includes('/listing/') && (
                    <MobileHeader
                      title="FASTCASHTRADE"
                      showLogo={false}
                      onSearchClick={() => navigate('/search')}
                    />
                  )}
                </div>

                <header className="hidden md:flex sticky top-0 z-[100] glass-effect px-4 h-14 md:h-16 items-center transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] pt-[var(--safe-top)]">
                  <nav className="max-w-7xl mx-auto flex items-center justify-between w-full">
                    <div
                      onClick={() => {
                        const isPaymentPage = location.pathname.includes('/deposit') ||
                          location.pathname.includes('/wallet/withdraw') ||
                          location.pathname.includes('/checkout');
                        if (!isPaymentPage) {
                          // Trigger animation on click by forcing re-render
                          const el = document.getElementById('fct-desktop-star-arc');
                          if (el) {
                            el.classList.remove('animate');
                            void (el as HTMLElement).offsetWidth; // Force reflow
                            el.classList.add('animate');
                          }
                          navigate('/');
                        }
                      }}
                      className="relative flex items-center cursor-pointer group select-none py-1 px-1 shrink-0 transition-all active:scale-95 duration-200"
                    >
                      {/* THE BRAND TEXT */}
                      <div className="flex items-center text-xl md:text-2xl font-black italic tracking-tighter relative z-10">
                        <span className="text-brand-clay dark:text-white transition-colors">
                          FASTCASH
                        </span>
                        <span className="text-brand-clay/40 dark:text-white/40 transition-colors ml-0.5">
                          TRADE
                        </span>
                      </div>

                      {/* THE DISNEY ARC - SVG Animation */}
                      <svg
                        id="fct-desktop-star-arc"
                        className="absolute inset-0 w-full h-full pointer-events-none overflow-visible animate"
                        viewBox="0 0 200 50"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ transform: 'scale(1.2)', transformOrigin: 'center' }}
                      >
                        <defs>
                          <linearGradient id="desktopHeaderArcGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="transparent" />
                            <stop offset="30%" className="[stop-color:#B8860B] dark:[stop-color:#FDFDFD]" stopOpacity="0.6" />
                            <stop offset="50%" stopColor="#D4AF37" stopOpacity="1" />
                            <stop offset="70%" className="[stop-color:#B8860B] dark:[stop-color:#FDFDFD]" stopOpacity="0.6" />
                            <stop offset="100%" stopColor="transparent" />
                          </linearGradient>
                          <filter id="desktopHeaderStarGlow" x="-100%" y="-100%" width="300%" height="300%">
                            <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
                            <feMerge>
                              <feMergeNode in="blur" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>

                        <motion.path
                          d="M 10 38 Q 100 -5 190 38"
                          fill="transparent"
                          stroke="url(#desktopHeaderArcGlow)"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          filter="url(#desktopHeaderStarGlow)"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: [0, 1], opacity: [0, 0.8, 0] }}
                          transition={{
                            duration: 1.5,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.5,
                            repeat: Infinity,
                            repeatDelay: 5
                          }}
                        />

                        <motion.circle
                          r="3"
                          className="fill-[#D4AF37] dark:fill-[#FDFDFD]"
                          filter="url(#desktopHeaderStarGlow)"
                          initial={{ opacity: 0, cx: 10, cy: 38 }}
                          animate={{
                            opacity: [0, 1, 1, 0],
                            cx: [10, 100, 190],
                            cy: [38, 5, 38]
                          }}
                          transition={{
                            duration: 1.5,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.5,
                            repeat: Infinity,
                            repeatDelay: 5,
                            times: [0, 0.5, 1]
                          }}
                          style={{
                            filter: 'drop-shadow(0 0 6px #D4AF37) drop-shadow(0 0 12px #B8860B)'
                          }}
                        />
                      </svg>
                    </div>

                    <div className="flex items-center gap-3 md:gap-5">
                      <Link to="/" className="hidden md:block text-sm font-medium text-brand-clay hover:text-brand-terracotta transition-colors">Marketplace</Link>

                      {/* Performance Toggle */}
                      <button
                        onClick={() => setIsLowPerf(!isLowPerf)}
                        className={`p-2 rounded-full transition-all active:scale-90 ${isLowPerf ? 'bg-brand-gold/10 text-brand-gold' : 'bg-transparent text-brand-clay/30 hover:text-brand-clay'}`}
                        title={isLowPerf ? "Yüksek Performans Moduna Geç" : "Düşük Performans Moduna Geç (Hızlandır)"}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                      </button>

                      {/* Favorites Link */}
                      {currentUser && (
                        <Link to="/favorites" className="hidden md:flex p-2 rounded-full text-brand-clay/60 hover:text-brand-terracotta hover:bg-brand-clay/5 transition-all relative active:scale-90">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                          {currentUser.favoritesCount && currentUser.favoritesCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 bg-brand-terracotta text-white text-[9px] font-bold flex items-center justify-center rounded-full w-3.5 h-3.5 shadow-sm">{currentUser.favoritesCount}</span>
                          )}
                        </Link>
                      )}

                      {/* Cart Link (Desktop) */}
                      {currentUser && (
                        <div className="hidden md:block">
                          <Link to="/cart" className="p-2 rounded-full text-brand-clay/60 hover:text-brand-terracotta hover:bg-brand-clay/5 transition-all relative active:scale-90">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                            <CartBadge />
                          </Link>
                        </div>
                      )}

                      {currentUser ? (
                        <>
                          <Link to="/messages" className="hidden md:flex p-2 rounded-full text-brand-clay/60 hover:text-brand-terracotta hover:bg-brand-clay/5 transition-all relative active:scale-90">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
                            {unreadMessageCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-brand-terracotta text-white text-[9px] font-bold flex items-center justify-center rounded-full shadow-sm">{unreadMessageCount}</span>}
                          </Link>

                          <div className="hidden md:block">
                            <NotificationCenter
                              userId={currentUser.id}
                              notifications={notifications}
                              onRefresh={fetchData}
                            />
                          </div>

                          <Link to="/wallet" className="hidden md:block text-[10px] font-black uppercase tracking-widest hover:text-brand-terracotta transition-colors">Cüzdanım</Link>

                          <Link to={`/profile/${currentUser.id}`} className="hidden md:block w-10 h-10 rounded-2xl overflow-hidden border-2 border-brand-terracotta hover:scale-105 transition-transform">
                            <img src={currentUser.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.name)}&background=C36B4F&color=fff`} className="w-full h-full object-cover" alt="" />
                          </Link>

                          <Link to="/sell" onMouseEnter={preloadSellChoice} className="hidden md:block px-6 py-3 bg-brand-terracotta text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all">İlan Ver</Link>
                        </>
                      ) : (
                        <Link to="/auth" className="px-5 md:px-8 py-2 md:py-3 bg-brand-clay text-white rounded-xl md:rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">Giriş Yap</Link>
                      )}
                    </div>
                  </nav>
                </header>

                <main className={`max-w-7xl mx-auto px-4 pt-24 pb-24 md:pt-20 md:pb-12 transition-all duration-1000 ${showSplash ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
                  <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => window.location.replace('/')}>
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route path="/" element={<Marketplace users={users} favorites={globalFavorites} currentUser={currentUser} onToggleFavorite={globalToggleFavorite} />} />
                        <Route path="/cart" element={<CartPage />} />
                        <Route path="/edit-listing/:id" element={<EditListingPage />} />
                        <Route path="/dashboard" element={<Suspense fallback={<DashboardSkeleton />}><UserDashboard user={currentUser} /></Suspense>} />
                        <Route path="/admin" element={<Suspense fallback={<AdminSkeleton />}><AdminPanel onUpdate={handleUpdateListingStatus} /></Suspense>} />
                        <Route path="/sell" element={<SellChoice />} />
                        <Route path="/sell/marketplace" element={<ListingFormWizard user={currentUser!} onAdd={fetchData} />} />
                        <Route path="/sell/instant" element={<InstantBuyWizard user={currentUser!} />} />
                        <Route path="/favorites" element={<FavoritesPage currentUser={currentUser} />} />
                        <Route path="/messages" element={<Messages currentUser={currentUser!} users={users} />} />
                        <Route path="/profile/:userId" element={<UserProfile users={users} currentUser={currentUser} favorites={globalFavorites} onUpdateUser={refreshUser} onLogout={() => auth.signOut()} onToggleFavorite={globalToggleFavorite} />} />
                        <Route path="/listing/:listingId" element={<ListingDetails users={users} currentUser={currentUser} messages={[]} onAction={handleUpdateListingStatus} onSendMessage={() => { }} onPurchase={fetchData} />} />
                        <Route path="/wallet" element={<WalletPage />} />
                        <Route path="/deposit" element={<DepositPage />} />
                        <Route path="/wallet/withdraw" element={<WithdrawalPage />} />

                        <Route path="/test-buggy" element={<BuggyComponent />} />
                        <Route path="/onboarding" element={<Onboarding onComplete={() => navigate('/')} />} />
                        <Route path="/help" element={<HelpCenterPage />} />
                        <Route path="/support" element={<SupportPage />} />
                        <Route path="/account/restrictions" element={<AccountRestrictionsPage />} />
                        <Route path="/settings/notifications" element={<NotificationPreferencesPage userId={currentUser?.id || ''} />} />
                        <Route path="/auth" element={<Auth onAuth={() => { }} />} />
                        <Route path="/intro" element={<LandingPage />} />
                        <Route path="/search" element={<SearchPage />} />
                        <Route path="/settings" element={<SettingsPage currentUser={currentUser} onUpdateUser={refreshUser} />} />
                        <Route path="/panel" element={currentUser ? <MobileDashboard /> : <Auth onAuth={() => { }} />} />
                        <Route path="/notifications" element={currentUser ? <NotificationsPage notifications={notifications} onRefresh={fetchData} /> : <Auth onAuth={() => { }} />} />
                        <Route path="/following" element={<FollowingPage users={users} favorites={[]} currentUser={currentUser} onToggleFavorite={() => { }} />} />
                        <Route path="/profile" element={currentUser ? <UserProfile users={users} currentUser={currentUser} favorites={[]} onUpdateUser={refreshUser} onLogout={() => auth.signOut()} onToggleFavorite={() => { }} /> : <Auth onAuth={() => { }} />} />
                        <Route path="/flash-sale" element={<FlashSalePage />} />
                        <Route path="/auth/action" element={<AuthAction />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </main>

                <LegalFooter />

                {/* Mobile Bottom Navigation */}
                {!location.pathname.includes('/listing/') &&
                  !location.pathname.includes('/sell') &&
                  !location.pathname.includes('/auth') &&
                  !location.pathname.includes('/intro') &&
                  !location.pathname.includes('/cart') &&
                  <BottomNav />
                }

                {/* Region Selection Overlay */}
                {needsSelection && <RegionSelectorModal />}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;
